import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';

export interface HttpLinkConfig {
  enabled: boolean;
  domain: string;
}

export interface ImgCloudConfigContextValue {
  httpLink: HttpLinkConfig | null;
  loading: boolean;
  error: Error | null;
}

const ImgCloudConfigContext = createContext<ImgCloudConfigContextValue | null>(null);

const HTTP_LINK_CONFIG_ENDPOINT = '/config/img-cloud-http-link';

const DEFAULT_HTTP_LINK_CONFIG: HttpLinkConfig = {
  enabled: false,
  domain: 'nons.fukit.cn',
};

interface ApiResponse<T> {
  success?: boolean;
  data?: T;
  message?: string;
}

const isValidDomain = (value: unknown): value is string => (
  typeof value === 'string'
  && /^[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*$/.test(value.trim())
);

export const loadHttpLinkConfig = async (): Promise<HttpLinkConfig> => {
  const response = await fetch(HTTP_LINK_CONFIG_ENDPOINT, {
    method: 'GET',
    credentials: 'include',
    cache: 'no-store',
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch HTTP link config: ${response.status}`);
  }

  const result = await response.json() as ApiResponse<HttpLinkConfig>;
  if (result.success !== true
    || typeof result.data?.enabled !== 'boolean'
    || !isValidDomain(result.data.domain)) {
    throw new Error(result.message || 'Invalid HTTP link config response');
  }

  return {
    enabled: result.data.enabled,
    domain: result.data.domain.trim(),
  };
};

export const ImgCloudConfigProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [httpLink, setHttpLink] = useState<HttpLinkConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let active = true;

    loadHttpLinkConfig()
      .then((config) => {
        if (active) {
          setHttpLink(config);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (active) {
          setError(err instanceof Error ? err : new Error('Failed to load HTTP link config'));
          setHttpLink(DEFAULT_HTTP_LINK_CONFIG);
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const contextValue: ImgCloudConfigContextValue = {
    httpLink,
    loading,
    error,
  };

  return (
    <ImgCloudConfigContext.Provider value={contextValue}>
      {children}
    </ImgCloudConfigContext.Provider>
  );
};

export const useImgCloudConfig = (): ImgCloudConfigContextValue => {
  const context = useContext(ImgCloudConfigContext);
  if (!context) {
    throw new Error('useImgCloudConfig must be used within ImgCloudConfigProvider');
  }
  return context;
};
